import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  CallEnd,
  Group,
  Mic,
  MicOff,
  PersonAdd,
  RadioButtonChecked,
  Stop as StopIcon,
  Videocam,
  VideocamOff,
  Wifi,
} from '@mui/icons-material';
import { Room, RoomEvent } from 'livekit-client';
import { browserLivekitUrlCandidates } from './livekitUrl';
import FaderStrip from './FaderStrip';
import InvitationDialog from './InvitationDialog';
import ParticipantMedia from './ParticipantMedia';
import liveStudioApi, { getLiveStudioError } from './liveStudioApi';
import {
  buildMixerChannels,
  getParticipantRole,
  patchMixerChannel,
} from './mixerUtils';

const credentialsFrom = (payload) => ({
  livekitUrl:
    payload?.livekitUrl ||
    payload?.url ||
    payload?.room?.livekitUrl ||
    payload?.credentials?.livekitUrl,
  token:
    payload?.token ||
    payload?.accessToken ||
    payload?.room?.token ||
    payload?.credentials?.token,
});

const roomIdFor = (room) => room?.id || room?.roomId || room?.sid || null;

const formatClock = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// The server reports its recorder state whenever a recorder exists, with
// `recording: false` between takes. Only an open take counts as recording.
export const openTakeState = (state) => (state && state.recording === true ? state : null);

const markerValue = (value) => (value === null || value === undefined ? '' : String(value));

/** Do two session contexts name the same voicetrack marker? */
export const sameMarker = (a, b) => (
  markerValue(a?.playlist_date) === markerValue(b?.playlist_date)
  && markerValue(a?.playlist_hour) === markerValue(b?.playlist_hour)
  && markerValue(a?.playlist_index) === markerValue(b?.playlist_index)
);

/** A take row from the server, tagged with the room it has to be saved through. */
export const takeFrom = (row, roomId = null) => (row?.id
  ? {
    ...row,
    status: row.status || 'ready',
    participant_count: row.participant_count ?? row.participantCount ?? null,
    roomId: row.room_id || row.roomId || roomId,
  }
  : null);

/**
 * The ready take recorded for this marker, newest first. A session holds one
 * take per marker, so the room's newest take is not necessarily this one's.
 */
export const takeForMarker = (takes, context) => (Array.isArray(takes) ? takes : [])
  .find((row) => row?.id && row.status === 'ready' && sameMarker(row.session_context, context)) || null;

/**
 * Co-presented voicetracking.
 *
 * Runs a Live Studio room whose purpose is `voicetrack`: the same invite, device
 * and mixer machinery, but every published microphone is captured into one take
 * on the server instead of being mixed into the station's broadcast output.
 * The presenter never records locally, so a guest's audio cannot be lost to a
 * browser hiccup on the host's machine.
 */
export default function VoicetrackSessionPanel({
  stationId,
  regionId = null,
  sessionContext = {},
  audioOutputId = '',
  // Whether co-presenting is allowed for the hour on screen. Gates starting a
  // session or a take only: an open session survives browsing to an hour
  // where it is off.
  available = true,
  unavailableReason = '',
  onSaved,
  onTakeFinished,
  onClose,
  api = liveStudioApi,
}) {
  const [room, setRoom] = useState(null);
  const [roomClient, setRoomClient] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [channels, setChannels] = useState(() => buildMixerChannels({}));
  const [connectionState, setConnectionState] = useState('disconnected');
  const [recording, setRecording] = useState(null);
  // The take offered for the marker on screen. It carries its own roomId, so
  // a take whose room has ended can still be saved without that room coming
  // back as the live session.
  const [take, setTake] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const roomRef = useRef(null);
  const connectGenerationRef = useRef(0);
  const endedRoomIdsRef = useRef(new Set());
  const sessionContextRef = useRef(sessionContext);
  sessionContextRef.current = sessionContext;

  const activeRoomId = roomIdFor(room);

  const syncParticipants = useCallback((client = roomRef.current) => {
    if (!client) {
      setParticipants([]);
      return;
    }
    setParticipants([
      client.localParticipant,
      ...Array.from(client.remoteParticipants.values()).slice(0, 6),
    ]);
  }, []);

  const disconnect = useCallback(async () => {
    connectGenerationRef.current += 1;
    const current = roomRef.current;
    roomRef.current = null;
    setRoomClient(null);
    setParticipants([]);
    setConnectionState('disconnected');
    if (current) {
      await Promise.resolve(current.disconnect()).catch(() => {});
    }
  }, []);

  const connect = useCallback(async ({ livekitUrl, token }) => {
    if (!livekitUrl || !token) throw new Error('The session did not provide connection credentials.');
    await disconnect();
    // Bumped by disconnect() and on unmount: a connect that has been
    // superseded must not try further URLs or keep a room it opened late.
    const generation = ++connectGenerationRef.current;
    const superseded = () => connectGenerationRef.current !== generation;
    const candidates = browserLivekitUrlCandidates(livekitUrl);
    if (!candidates.length) throw new Error('The session did not provide a reachable signaling URL.');

    setConnectionState('connecting');
    let lastError = null;
    let previousAttempt = null;
    for (const url of candidates) {
      if (superseded()) break;
      const attempt = new Room({ adaptiveStream: true, dynacast: true });
      // Becomes current before the failed attempt is torn down, so that
      // attempt's Disconnected event is ignored instead of resetting state.
      roomRef.current = attempt;
      setRoomClient(attempt);
      if (previousAttempt) await Promise.resolve(previousAttempt.disconnect()).catch(() => {});
      previousAttempt = attempt;
      const isCurrent = () => roomRef.current === attempt;
      const sync = () => {
        if (isCurrent()) syncParticipants(attempt);
      };
      [
        RoomEvent.ParticipantConnected,
        RoomEvent.ParticipantDisconnected,
        RoomEvent.TrackSubscribed,
        RoomEvent.TrackUnsubscribed,
        RoomEvent.TrackMuted,
        RoomEvent.TrackUnmuted,
        RoomEvent.LocalTrackPublished,
        RoomEvent.LocalTrackUnpublished,
        RoomEvent.ActiveSpeakersChanged,
      ].forEach((eventName) => attempt.on(eventName, sync));
      attempt.on(RoomEvent.Disconnected, () => {
        if (!isCurrent()) return;
        setConnectionState('disconnected');
        setParticipants([]);
      });
      try {
        await attempt.connect(url, token);
        if (superseded()) {
          await Promise.resolve(attempt.disconnect()).catch(() => {});
          return false;
        }
        lastError = null;
        setConnectionState('connected');
        if (audioOutputId && typeof attempt.switchActiveDevice === 'function') {
          await attempt.switchActiveDevice('audiooutput', audioOutputId).catch(() => {});
        }
        sync();
        return true;
      } catch (connectError) {
        lastError = connectError;
      }
    }
    if (previousAttempt) await Promise.resolve(previousAttempt.disconnect()).catch(() => {});
    if (superseded()) return false;
    roomRef.current = null;
    setRoomClient(null);
    setConnectionState('disconnected');
    throw new Error(`${lastError?.message || 'Could not connect'} Tried ${candidates.join(', ')}`);
  }, [audioOutputId, disconnect, syncParticipants]);

  const refreshRoom = useCallback(async (roomId) => {
    const details = await api.getRoom(roomId);
    // A poll still in flight when the session ended must not bring it back.
    if (endedRoomIdsRef.current.has(roomId) || details?.status === 'ended') return null;
    setRoom(details || null);
    return details;
  }, [api]);

  const refreshRecording = useCallback(async (roomId) => {
    try {
      const payload = await api.getSessionRecording(roomId);
      setRecording(openTakeState(payload?.recording));
      const rows = Array.isArray(payload?.takes) ? payload.takes : [];
      const match = takeForMarker(rows, sessionContextRef.current);
      setTake((current) => {
        if (match) return takeFrom(match, roomId);
        // This room's take was saved or discarded elsewhere: stop offering it.
        const stillReady = rows.some((row) => row?.id === current?.id && row.status === 'ready');
        if (current?.roomId === roomId && current.status === 'ready' && !stillReady) return null;
        return current;
      });
    } catch {
      // Recording state is advisory; the room itself is the source of truth.
    }
  }, [api]);

  const startSession = useCallback(async () => {
    if (stationId === '' || stationId === null || stationId === undefined) {
      setError('Select a station before starting a voicetrack session.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const payload = await api.createRoom({
        stationId,
        regionId,
        purpose: 'voicetrack',
        sessionContext: sessionContextRef.current,
      });
      const roomId = roomIdFor(payload) || roomIdFor(payload?.room);
      setRoom(payload?.room && typeof payload.room === 'object' ? payload.room : payload);
      if (roomId) {
        const details = await refreshRoom(roomId).catch(() => null);
        if (details) setRoom(details);
        await refreshRecording(roomId);
      }

      const credentials = credentialsFrom(payload);
      if (credentials.token) {
        await connect(credentials);
      } else if (roomId) {
        const hostToken = await api.createRoomToken(roomId);
        await connect(credentialsFrom(hostToken));
      } else {
        throw new Error('The session did not return a room identifier.');
      }
      setNotice('Voicetrack session started. Invite your co-presenter.');
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not start the voicetrack session.'));
    } finally {
      setBusy(false);
    }
  }, [api, connect, refreshRecording, refreshRoom, regionId, stationId]);

  // Adopt an existing session when the presenter reloads the page mid-take.
  // Only runs when there is no session in this component already.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (stationId === '' || stationId === null || stationId === undefined) return;
      try {
        const payload = await api.getActiveRoom(stationId, regionId, 'voicetrack');
        const roomId = roomIdFor(payload) || roomIdFor(payload?.room);
        if (!roomId || cancelled) return;
        setBusy(true);
        const details = await refreshRoom(roomId).catch(() => null);
        if (cancelled) return;
        if (details) setRoom(details);
        await refreshRecording(roomId);
        if (details?.can_manage) {
          const tokenPayload = await api.createRoomToken(roomId);
          if (!cancelled) await connect(credentialsFrom(tokenPayload));
        }
      } catch {
        // 404 simply means there is no session for this hour.
      } finally {
        if (!cancelled) setBusy(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [api, connect, refreshRecording, refreshRoom, regionId, stationId]);

  // A take can outlive its room: if the presenter ended the session and then
  // reloaded, offer the finished recording rather than losing it. The lookup is
  // per marker, so browsing to another voicetrack slot clears the offer instead
  // of leaving the previous slot's take on screen.
  useEffect(() => {
    let cancelled = false;
    // The previous marker's take must not stay on screen, or be saved, here.
    setTake(null);
    const findTake = async () => {
      if (stationId === '' || stationId === null || stationId === undefined) return;
      const context = sessionContextRef.current || {};
      if (!context.playlist_date || context.playlist_hour === null || context.playlist_hour === undefined) return;
      try {
        const found = takeFrom(await api.getSessionTake({
          stationId,
          playlistDate: context.playlist_date,
          playlistHour: context.playlist_hour,
          playlistIndex: context.playlist_index ?? null,
        }));
        if (cancelled || found?.status !== 'ready') return;
        if (found.session_context && !sameMarker(found.session_context, context)) return;
        // An exact match the session poll found meanwhile wins.
        setTake((current) => current || found);
      } catch {
        // 404 just means nothing is waiting to be saved for this slot.
      }
    };
    findTake();
    return () => { cancelled = true; };
  }, [
    api,
    stationId,
    sessionContext.playlist_date,
    sessionContext.playlist_hour,
    sessionContext.playlist_index,
  ]);

  useEffect(() => {
    if (!activeRoomId) return undefined;
    const interval = setInterval(() => refreshRecording(activeRoomId), 4000);
    return () => clearInterval(interval);
  }, [activeRoomId, refreshRecording]);

  // Local elapsed timer so the presenter sees a clock even between polls.
  // The server value stays authoritative: each poll resets the display.
  const isRolling = Boolean(recording?.startedAt);
  useEffect(() => {
    if (recording?.startedAt) setElapsed(Number(recording.durationSeconds) || 0);
  }, [recording?.startedAt, recording?.durationSeconds]);

  useEffect(() => {
    if (!isRolling) return undefined;
    const tick = setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => clearInterval(tick);
  }, [isRolling]);

  useEffect(() => {
    if (!room) {
      setChannels(buildMixerChannels({}));
      return;
    }
    const liveParticipants = participants.map((participant, index) => ({
      ...participant,
      role: index === 0 ? 'host' : undefined,
    }));
    const incoming = buildMixerChannels({
      channels: room.channels,
      participants: [...(room.participants || []), ...liveParticipants],
    });
    // A voicetrack take has no program bus: nothing from the station's playout
    // is part of the recording, so a Program strip would be misleading.
    const withoutProgram = incoming.filter((channel) => channel.role !== 'program');
    setChannels((current) => withoutProgram.map((channel) => {
      const previous = current.find((item) => String(item.id) === String(channel.id));
      return previous ? { ...previous, ...channel, meter: channel.meter ?? previous.meter } : channel;
    }));
  }, [participants, room]);

  useEffect(() => {
    if (!activeRoomId) return undefined;
    const interval = setInterval(() => {
      refreshRoom(activeRoomId).catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [activeRoomId, refreshRoom]);

  useEffect(() => () => {
    connectGenerationRef.current += 1;
    const current = roomRef.current;
    roomRef.current = null;
    if (current) Promise.resolve(current.disconnect()).catch(() => {});
  }, []);

  const toggleMicrophone = async () => {
    const local = roomClient?.localParticipant;
    if (!local) return;
    try {
      await local.setMicrophoneEnabled(!local.isMicrophoneEnabled);
      syncParticipants(roomClient);
    } catch (mediaError) {
      setError(getLiveStudioError(mediaError, 'Microphone access failed.'));
    }
  };

  const toggleCamera = async () => {
    const local = roomClient?.localParticipant;
    if (!local) return;
    try {
      await local.setCameraEnabled(!local.isCameraEnabled);
      syncParticipants(roomClient);
    } catch (mediaError) {
      setError(getLiveStudioError(mediaError, 'Camera access failed.'));
    }
  };

  const startRecording = async () => {
    if (!activeRoomId) return;
    const context = sessionContextRef.current || {};
    setBusy(true);
    setError('');
    try {
      const state = await api.startSessionRecording(activeRoomId, {
        // Each take records which marker it belongs to, so a session that spans
        // several hours keeps its voicetracks apart.
        sessionContext: {
          playlist_date: context.playlist_date ?? null,
          playlist_hour: context.playlist_hour ?? null,
          playlist_index: context.playlist_index ?? null,
          playout_log_id: context.playout_log_id ?? null,
        },
        label: context.playlist_date && context.playlist_hour !== null && context.playlist_hour !== undefined
          ? `${context.playlist_date} ${String(context.playlist_hour).padStart(2, '0')}:00`
          : null,
      });
      setRecording(state
        ? openTakeState(state)
        : { recording: true, startedAt: new Date().toISOString(), durationSeconds: 0 });
      setElapsed(Number(state?.durationSeconds) || 0);
      setNotice('Recording. Every presenter in the session is going into this take.');
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not start recording.'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Stop the take but keep the session open.
   *
   * This is the whole point of Multi Voicetrack: presenters stay in the room
   * and record the next hour's voicetrack without rejoining.
   */
  const stopRecording = async () => {
    if (!activeRoomId) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.stopSessionRecording(activeRoomId);
      setRecording(null);
      setElapsed(0);
      const finished = takeFrom(result?.take, activeRoomId);
      if (finished) {
        // The take belongs to the marker it was started for, which may no
        // longer be the one on screen.
        if (!finished.session_context || sameMarker(finished.session_context, sessionContextRef.current)) {
          setTake(finished);
          setNotice('Take finished. Save it to this slot, or record the next one.');
        } else {
          setNotice('Take finished for another slot. Select that slot to save it.');
        }
        onTakeFinished?.(result.take);
      } else {
        setNotice('Take finished, but nothing was captured.');
      }
      await refreshRecording(activeRoomId).catch(() => {});
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not stop the recording.'));
    } finally {
      setBusy(false);
    }
  };

  const updateChannel = async (channelId, changes) => {
    if (!activeRoomId) return;
    let snapshot = null;
    setChannels((current) => {
      snapshot = current;
      return patchMixerChannel(current, channelId, changes);
    });
    try {
      const result = await api.updateChannel(activeRoomId, channelId, changes);
      const serverChannel = result?.channel || result;
      if (serverChannel) {
        setChannels((current) => patchMixerChannel(current, channelId, serverChannel));
      }
    } catch (requestError) {
      if (snapshot) setChannels(snapshot);
      setError(getLiveStudioError(requestError, 'Could not change that fader.'));
    }
  };

  const endSession = async () => {
    if (!activeRoomId) return;
    const prompt = recording
      ? 'End this session and finish the recording?'
      : 'Close this session? Nothing has been recorded yet.';
    if (!window.confirm(prompt)) return;
    const closingRoomId = activeRoomId;
    setBusy(true);
    setError('');
    try {
      const result = await api.endRoom(closingRoomId);
      // The ended room must not come back as the live session; a take that
      // still needs saving carries its own room id.
      endedRoomIdsRef.current.add(closingRoomId);
      setRoom(null);
      await disconnect();
      setRecording(null);
      const finished = takeFrom(result?.recording, closingRoomId);
      if (finished && (!finished.session_context || sameMarker(finished.session_context, sessionContextRef.current))) {
        setTake(finished);
        setNotice('Recording finished. Save it to this hour to use it.');
      } else {
        await refreshRecording(closingRoomId);
        setNotice('Session closed.');
      }
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not end the session.'));
    } finally {
      setBusy(false);
    }
  };

  const saveTake = async () => {
    const saveRoomId = take?.roomId || activeRoomId;
    if (!take?.id || !saveRoomId) {
      setError('This take is no longer attached to a session.');
      return;
    }
    const takeId = take.id;
    const context = sessionContextRef.current || {};
    setBusy(true);
    setError('');
    try {
      const saved = await api.saveSessionVoicetrack(saveRoomId, {
        // Name the take: without it the server would pick by its own rules,
        // which may be another marker's take.
        take_id: takeId,
        station_id: stationId,
        region_id: regionId || null,
        playlist_date: context.playlist_date || null,
        playlist_hour: context.playlist_hour ?? null,
        playlist_index: context.playlist_index ?? null,
        playout_log_id: context.playout_log_id ?? null,
        content: 'Voicetrack',
        is_sweeper: !!context.use_as_sweeper,
        play_as: context.use_as_sweeper ? 'sweeper' : 'normal',
        ai_settings: context.ai_settings || {},
        vt_position: context.vt_position,
        vt_gap: context.vt_gap,
        volumes: context.volumes,
        segue: context.segue,
      });
      setNotice('Voicetrack saved.');
      setTake((current) => (current?.id === takeId ? { ...current, status: 'claimed' } : current));
      onSaved?.(saved);
    } catch (requestError) {
      setError(getLiveStudioError(requestError, 'Could not save the voicetrack.'));
    } finally {
      setBusy(false);
    }
  };

  const connected = connectionState === 'connected';
  const canManage = room?.can_manage === true;
  const localParticipant = roomClient?.localParticipant;
  const guestCount = (participants || []).filter(
    (participant) => participant !== localParticipant && getParticipantRole(participant) === 'guest'
  ).length;

  return (
    <Card sx={{ border: '1px solid var(--rs-border)', bgcolor: 'var(--rs-paper)' }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Group sx={{ color: 'var(--rs-accent)' }} />
          <Box sx={{ flex: 1, minWidth: 160 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Co-presented voicetrack</Typography>
            <Typography variant="caption" color="text.secondary">
              Record this take with another presenter. Every microphone in the session is captured into one file.
            </Typography>
          </Box>
          {recording ? (
            <Chip
              size="small"
              icon={<RadioButtonChecked />}
              color="error"
              label={`Recording ${formatClock(elapsed)}`}
            />
          ) : null}
          {activeRoomId ? (
            <Chip
              size="small"
              icon={<Wifi />}
              color={connected ? 'success' : 'warning'}
              label={connected ? 'Connected' : connectionState}
            />
          ) : null}
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5 }}>{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 1.5 }}>{notice}</Alert>}

        {!activeRoomId && !take && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Start a session, invite the other presenter and check devices. Press Record when you are both
              ready: everything after that goes into one file, recorded on the server.
            </Typography>
            {!available && (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {unavailableReason || 'Co-presented voicetracks are not enabled for this hour.'}
              </Alert>
            )}
            <Button
              variant="contained"
              onClick={startSession}
              disabled={busy || !available}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Group />}
            >
              {busy ? 'Starting…' : 'Start voicetrack session'}
            </Button>
          </Box>
        )}

        {activeRoomId && (
          <Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Button
                size="small"
                variant={localParticipant?.isMicrophoneEnabled ? 'contained' : 'outlined'}
                color={localParticipant?.isMicrophoneEnabled ? 'success' : 'inherit'}
                startIcon={localParticipant?.isMicrophoneEnabled ? <Mic /> : <MicOff />}
                onClick={toggleMicrophone}
                disabled={!connected}
              >
                Mic
              </Button>
              <Button
                size="small"
                variant={localParticipant?.isCameraEnabled ? 'contained' : 'outlined'}
                color={localParticipant?.isCameraEnabled ? 'info' : 'inherit'}
                startIcon={localParticipant?.isCameraEnabled ? <Videocam /> : <VideocamOff />}
                onClick={toggleCamera}
                disabled={!connected}
              >
                Camera
              </Button>
              {canManage && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAdd />}
                  onClick={() => setInviteOpen(true)}
                  disabled={guestCount >= 5}
                >
                  Invite co-presenter
                </Button>
              )}
              {canManage && !recording && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<RadioButtonChecked />}
                  onClick={startRecording}
                  disabled={busy || !available}
                >
                  Record
                </Button>
              )}
              {canManage && recording && (
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  onClick={stopRecording}
                  disabled={busy}
                >
                  Stop take
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CallEnd />}
                onClick={endSession}
                disabled={busy}
              >
                End session
              </Button>
            </Stack>
            {canManage && !recording && !available && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.75, mb: 1.5 }}>
                {unavailableReason || 'Co-presented voicetracks are not enabled for this hour.'} The session stays open.
              </Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--rs-muted)' }}>
                  IN THE SESSION
                </Typography>
                <ParticipantMedia
                  participants={participants}
                  localParticipant={localParticipant}
                  audioOutputId={audioOutputId}
                />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--rs-muted)' }}>
                  RECORDED MIX
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  These faders set the balance written into the voicetrack. Mute removes a presenter from the take only.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                  {channels.map((channel) => (
                    <FaderStrip
                      key={channel.id}
                      channel={channel}
                      disabled={!canManage}
                      onGainPreview={(channelId, gain) =>
                        setChannels((current) => patchMixerChannel(current, channelId, { gain }))
                      }
                      onGainCommit={(channelId, gain) => updateChannel(channelId, { gain })}
                      onToggleMute={(item) => updateChannel(item.id, { muted: !item.muted })}
                      onTogglePfl={() => {}}
                      onToggleOnAir={(item) => updateChannel(item.id, { onAir: !item.onAir })}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {take && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" color="success" label={`Take ready · ${formatClock(take.duration)}`} />
              {take.participant_count ? (
                <Typography variant="caption" color="text.secondary">
                  {take.participant_count} presenter{take.participant_count === 1 ? '' : 's'} captured
                </Typography>
              ) : null}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="contained"
                onClick={saveTake}
                disabled={busy || take.status === 'claimed'}
              >
                {take.status === 'claimed' ? 'Saved' : 'Save to this hour'}
              </Button>
              {onClose && (
                <Button size="small" onClick={onClose}>Close</Button>
              )}
            </Box>
          </>
        )}
      </CardContent>

      {canManage && (
        <InvitationDialog
          open={inviteOpen}
          roomId={activeRoomId}
          invites={room?.invites || []}
          onChanged={async () => {
            if (!activeRoomId) return;
            await refreshRoom(activeRoomId).catch(() => {});
          }}
          onClose={() => setInviteOpen(false)}
          api={api}
        />
      )}
    </Card>
  );
}
